/**
 * Epic Analytics Dashboard Constants
 *
 * Shared constants and configuration values for the Epic Analytics Dashboard
 */

import { QuarterConfig, YearRange } from "../types/epic-analytics-types";

// ===== Time Period Constants =====

/**
 * Current year for default selections
 */
export const CURRENT_YEAR = new Date().getFullYear();

/**
 * Year range configuration (current year ± 2 years)
 */
export const YEAR_RANGE: YearRange = {
  currentYear: CURRENT_YEAR,
  minYear: CURRENT_YEAR - 2,
  maxYear: CURRENT_YEAR + 2,
};

/**
 * Quarter configurations with labels and date ranges
 */
export const QUARTER_CONFIGS: QuarterConfig[] = [
  {
    value: "all",
    label: "All Quarters",
    startMonth: 1,
    endMonth: 12,
  },
  {
    value: 1,
    label: "Q1 (Jan-Mar)",
    startMonth: 1,
    endMonth: 3,
  },
  {
    value: 2,
    label: "Q2 (Apr-Jun)",
    startMonth: 4,
    endMonth: 6,
  },
  {
    value: 3,
    label: "Q3 (Jul-Sep)",
    startMonth: 7,
    endMonth: 9,
  },
  {
    value: 4,
    label: "Q4 (Oct-Dec)",
    startMonth: 10,
    endMonth: 12,
  },
];

// ===== Chart Configuration Constants =====

/**
 * Default colors for charts
 */
export const CHART_COLORS = {
  primary: "#3B82F6", // Blue-500
  secondary: "#10B981", // Emerald-500
  accent: "#F59E0B", // Amber-500
  danger: "#EF4444", // Red-500
  warning: "#F97316", // Orange-500
  success: "#22C55E", // Green-500
  muted: "#6B7280", // Gray-500
} as const;

/**
 * Chart theme configuration
 */
export const CHART_THEME = {
  backgroundColor: "rgba(59, 130, 246, 0.1)", // primary with opacity
  borderColor: CHART_COLORS.primary,
  pointBackgroundColor: CHART_COLORS.primary,
  pointBorderColor: "#FFFFFF",
  pointHoverBackgroundColor: CHART_COLORS.secondary,
  pointHoverBorderColor: CHART_COLORS.primary,
} as const;

/**
 * Bubble chart specific colors
 */
export const BUBBLE_CHART_COLORS = {
  storyPoints: {
    background: "rgba(59, 130, 246, 0.6)", // primary with opacity
    border: CHART_COLORS.primary,
  },
  bugs: {
    background: "rgba(239, 68, 68, 0.6)", // danger with opacity
    border: CHART_COLORS.danger,
  },
} as const;

// ===== Layout Constants =====

/**
 * Responsive breakpoints
 */
export const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
  desktop: 1280,
} as const;

/**
 * Layout proportions
 */
export const LAYOUT_PROPORTIONS = {
  chartsWidth: "66.666667%", // 2/3 width
  listWidth: "33.333333%", // 1/3 width
  mobileStackBreakpoint: BREAKPOINTS.mobile,
} as const;

// ===== API Constants =====

/**
 * API endpoints for epic analytics
 */
export const API_ENDPOINTS = {
  epicAnalytics: "/api/epics/analytics",
} as const;

/**
 * Query configuration
 */
export const QUERY_CONFIG = {
  staleTime: 15 * 60 * 1000, // 15 minutes
  cacheTime: 30 * 60 * 1000, // 30 minutes
  refetchOnWindowFocus: false,
  retry: 2,
} as const;

// ===== Navigation Constants =====

/**
 * Epic navigation tabs
 */
export const EPIC_NAVIGATION_TABS = {
  analysis: {
    value: "analysis" as const,
    label: "Analysis",
    path: "/epics",
  },
  reports: {
    value: "reports" as const,
    label: "Reports",
    path: "/epics/analysis",
  },
} as const;

// ===== Validation Constants =====

/**
 * Validation rules for filters
 */
export const VALIDATION_RULES = {
  year: {
    min: YEAR_RANGE.minYear,
    max: YEAR_RANGE.maxYear,
  },
  quarter: {
    validValues: ["all", 1, 2, 3, 4] as const,
  },
} as const;

// ===== Default Values =====

/**
 * Default filter values
 */
export const DEFAULT_FILTERS = {
  year: CURRENT_YEAR,
  quarter: "all" as const,
} as const;

/**
 * Default chart options
 */
export const DEFAULT_CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: "top" as const,
    },
    tooltip: {
      enabled: true,
      mode: "index" as const,
      intersect: false,
    },
  },
  interaction: {
    mode: "nearest" as const,
    axis: "x" as const,
    intersect: false,
  },
} as const;

// ===== Error Messages =====

/**
 * Error messages for epic analytics
 */
export const ERROR_MESSAGES = {
  noProject: "Please select a project to view analytics",
  noData: "No epic data found for the selected filters",
  loadingError: "Failed to load epic analytics data",
  invalidFilters: "Invalid filter configuration",
  networkError: "Network error occurred while loading data",
} as const;

// ===== Loading States =====

/**
 * Loading state messages
 */
export const LOADING_MESSAGES = {
  loadingData: "Loading epic analytics data...",
  loadingChart: "Rendering chart...",
  loadingFilters: "Loading filter options...",
} as const;
